import { formatStructuredClinicalLimit, clinicalLimitStatus } from './clinicalTargetSemantics';
import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  formatMetric,
  type AnalysisData,
  type MealPlanClinicalLimit,
  type MealPlanClinicalTargetRange,
  type MealPlanNutritionReportDetail,
  safeNumber,
} from './analysis.shared';
import { type DrvPrimaryType } from '@/lib/api/drv';
import {
  NutritionReportSectionTable,
  type NutritionReportRow,
  type NutritionSection,
  type RowFieldValue,
  type RowTone,
  type SuggestedRdaColumn,
  type ToneStyles,
} from './NutritionReportSectionTable';
import {
  getSuggestedRdaProgressForGramRange,
  getSuggestedRdaRowTone,
  getSuggestedRdaRowToneForGramRange,
  getSuggestedRdaToneFromPercent,
  suggestedRdaToneStyles,
} from './suggestedRdaTone';

import type { Client } from '@/types/client';
import { getMealPlanNutritionalAnalysisFoods } from '@/lib/api/mealPlans';
import {
  findDrvRequirementByNutrientIds,
  resolveDrvRecommendedValue,
} from '@/utils/drv-resolve-primary-target';
import { showErrorToast } from '@/utils/toast-utils';
import type {
  NutritionalAnalysisMicronutrientItem,
  NutritionalAnalysisMicrosView,
} from './mergeMealPlanNutritionalAnalysis';
import {
  buildFoodContributionsForReportRow,
  nutrientQueryParamFromRow,
  parseNutritionalAnalysisFoodsResponse,
  type FoodContributionResult,
} from './nutrientReportFoodContributions';
import { NutritionReportPdfView } from './NutritionReportPdfView';

export type NutritionReportTableVariant = 'nutrition-report' | 'suggested-rda';

interface NutritionReportTableProps {
  analysisData: AnalysisData;
  microsView?: NutritionalAnalysisMicrosView | null;
  clientDisplayName?: string;
  subtitleParts?: string[];
  variant?: NutritionReportTableVariant;
  primaryTypes?: DrvPrimaryType[];
  client?: Client;
  drvRequirements?: unknown;
  /** For per-food detail when clicking a row (Nutrient Report). */
  nutritionalApiResponse?: unknown;
  viewMode?: number;
  selectedDayIndex?: number;
  /** Selected meal plan id for GET .../nutritional-analysis/foods */
  mealPlanId?: string;
  onExportReport?: () => void;
  exportReportLoading?: boolean;
  /** PDF export mode: hide interactive controls for stable rendering. */
  isPdfExport?: boolean;
  /** Optional macro distribution table+pie block inserted after PDF summary cards. */
  pdfMacroDistributionBlock?: React.ReactNode;
  /** PDF only: same layout as full nutrient report, but limit sections (Macros tab export). */
  pdfExportVariant?: 'full' | 'macros-only';
  /** From GET nutritional-analysis `hiddenNutrients` (normalized keys). */
  hiddenNutrientKeys?: ReadonlySet<string>;
  /** POST hide-nutrients / unhide-nutrients then refetch analysis; used for Hide/Unhide in the expand column. */
  onNutrientVisibilityChange?: (args: { nutrientKey: string; hide: boolean }) => void | Promise<void>;
  suggestedRdaMacrosAction?: React.ReactNode;
}

/** Sections kept when exporting PDF from Macros (same `NutritionReportPdfView` UI as full report). */
const PDF_MACRO_ONLY_SECTION_IDS = new Set([
  'energy',
  'macronutrients',
  'carb-detail',
  'lipid-components',
]);

const MINERALS_TRACE_IDS = [
  'sodium',
  'potassium',
  'chloride',
  'calcium',
  'phosphorus',
  'magnesium',
  'iron',
  'zinc',
  'copper',
  'manganese',
  'selenium',
  'iodine',
] as const;

const VITAMIN_IDS = [
  'vitamin_a',
  'vitamin_d',
  'vitamin_e',
  'vitamin_k',
  'vitamin_k1',
  'vitamin_b1',
  'vitamin_b2',
  'vitamin_b3',
  'vitamin_b5',
  'vitamin_b6',
  'folate',
  'vitamin_b12',
  'biotin',
  'vitamin_c',
] as const;

const INCLUDED_MICRO_IDS = new Set<string>([...VITAMIN_IDS, ...MINERALS_TRACE_IDS]);
const EXCLUDED_MICRO_IDS = new Set<string>([
  'water',
  'water_total',
  'total_water',
  'cholesterol',
]);

/** Lipid Components rows; same nutrients must not repeat under Other Micronutrients. */
const LIPID_COMPONENT_MICRO_IDS = new Set(['omega_3', 'omega_6', 'ala', 'la']);

function normalizeNutrientIdKey(id: string): string {
  return id.trim().toLowerCase().replace(/\s+/g, '_');
}

function isEnergyNutritionReportRowForPdf(row: NutritionReportRow): boolean {
  return row.nutrientApiKey === 'energy' || row.id === 'energy-row';
}

/** Omit server-hidden nutrients from PDF tables; Energy is always kept. Empty sections are dropped. */
function omitHiddenNutrientsFromNutritionSectionsForPdf(
  sections: NutritionSection[],
  hiddenNutrientKeys: ReadonlySet<string>,
): NutritionSection[] {
  if (!hiddenNutrientKeys.size) return sections;
  return sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) => {
        if (isEnergyNutritionReportRowForPdf(row)) return true;
        const key = nutrientQueryParamFromRow(row);
        if (!key) return true;
        return !hiddenNutrientKeys.has(key);
      }),
    }))
    .filter((s) => s.rows.length > 0);
}

function findMicrosItemByNutrientIds(
  microsView: NutritionalAnalysisMicrosView | null | undefined,
  nutrientIdCandidates: readonly string[],
): NutritionalAnalysisMicronutrientItem | undefined {
  const all = microsView?.byStatus?.all;
  if (!all?.length) return undefined;
  const wanted = new Set(nutrientIdCandidates.map((x) => normalizeNutrientIdKey(x)));
  return all.find((m) => wanted.has(normalizeNutrientIdKey(m.nutrientId)));
}

/** API `target` / RDA / AI is parsed into `rdaValue` in `toMicrosSlice`. */
function microsTargetGramsFromApi(item: NutritionalAnalysisMicronutrientItem | undefined): number | null {
  const v = item?.rdaValue;
  return Number.isFinite(v) && (v as number) > 0 ? (v as number) : null;
}

/** Prefer micros `actual` / `intakeValue`; otherwise meal-plan `nutrition` overlay. */
function omegaIntakeFromMicrosOrDetail(
  microItem: NutritionalAnalysisMicronutrientItem | undefined,
  detailGrams: { grams: number; intakeDisplay?: string },
): { intake: number; intakeDisplay?: string } {
  const fromApi = microItem?.intakeValue;
  if (fromApi != null && Number.isFinite(fromApi) && fromApi >= 0) {
    return { intake: fromApi };
  }
  return { intake: detailGrams.grams, intakeDisplay: detailGrams.intakeDisplay };
}

function rowToneFromMicronutrientStatus(
  status: NutritionalAnalysisMicronutrientItem['status'] | undefined,
): RowTone | undefined {
  if (status === 'adequate') return 'met';
  if (status === 'low') return 'deficient';
  if (status === 'excess') return 'excess';
  if (status === 'toxic') return 'toxic';
  if (status === 'unknown') return 'na';
  return undefined;
}

/** UL as `<x unit`; if UL is unavailable, show placeholder. */
function formatMicroNutrientLimits(m: NutritionalAnalysisMicronutrientItem, unit: string): string {
  const ul = m.ulValue;
  if (ul != null && Number.isFinite(ul) && ul > 0) {
    return `< ${formatMetric(ul, getDecimals(unit, ul))} ${unit}`;
  }
  return '--';
}

const reportToneStyles: ToneStyles = {
  met: {
    label: 'Met',
    color: '#067647',
    bg: '#ECFDF3',
    bar: '#12B76A',
  },
  borderline: {
    label: 'Borderline',
    color: '#B54708',
    bg: '#FFFAEB',
    bar: '#F79009',
  },
  deficient: {
    label: 'Deficient',
    color: '#B42318',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  excess: {
    label: 'Excess',
    color: '#B42318',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  toxic: {
    label: 'Toxic',
    color: '#B42318',
    bg: '#FEF3F2',
    bar: '#F04438',
  },
  na: {
    label: 'N/A',
    color: '#667085',
    bg: '#F2F4F7',
    bar: '#D0D5DD',
  },
};

const suggestedRdaColumns: SuggestedRdaColumn[] = [
  { key: 'unit', label: 'Unit', minWidth: 82, align: 'center' },
  { key: 'recommended', label: 'Recommended RDA', minWidth: 140, align: 'center' },
  { key: 'currentPlan', label: 'Client Goal (grams)', minWidth: 120, align: 'center' },
  { key: 'progress', label: '% Met', minWidth: 184, align: 'left', type: 'progress' },
  { key: 'status', label: 'Status', minWidth: 126, align: 'center', type: 'status' },
];

const suggestedRdaMacroColumns: SuggestedRdaColumn[] = [
  { key: 'percentage', label: 'Percentage', minWidth: 110, align: 'center' },
  { key: 'recommended', label: 'DRV Suggestion', minWidth: 160, align: 'center' },
  { key: 'unit', label: 'Unit', minWidth: 82, align: 'center' },
];

const suggestedRdaMicronutrientColumns: SuggestedRdaColumn[] = [
  { key: 'recommended', label: 'Recommended RDA', minWidth: 140, align: 'center' },
  { key: 'unit', label: 'Unit', minWidth: 82, align: 'center' },
];

const reportColumns: SuggestedRdaColumn[] = [
  { key: 'intake', label: 'Intake', minWidth: 96, align: 'center' },
  { key: 'target', label: 'Target', minWidth: 96, align: 'center' },
  { key: 'limits', label: 'Limits', minWidth: 96, align: 'center' },
  { key: 'progress', label: 'Progress', minWidth: 170, align: 'left', type: 'progress' },
  { key: 'status', label: 'Status', minWidth: 96, align: 'center', type: 'status' },
  { key: 'expand', label: '', minWidth: 88, align: 'left', type: 'expand' },
];

function getKpiByLabel(analysisData: AnalysisData, label: string) {
  const normalized = label.trim().toLowerCase();
  return analysisData.kpis.find((item) => item.label.trim().toLowerCase() === normalized);
}

function getDecimals(unit: string, value: number): number {
  const normalized = unit.trim().toLowerCase();
  if (normalized === 'l') return 1;
  if (normalized === 'mg') return 0;
  if (Math.abs(value) < 10) return 1;
  return 0;
}

function toPositiveNumber(value: unknown): number | null {
  const numeric = safeNumber(value, Number.NaN);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function normalizeDrvUnit(value: unknown, fallback: string): string {
  const unit = String(value ?? '').trim();
  return unit || fallback;
}

function normalizeDrvLookupKey(value: string): string {
  return value.replace(/[_\s-]/g, '').toLowerCase();
}

function unwrapDrvRequirementRoot(drvRequirements: unknown): Record<string, unknown> | null {
  if (!drvRequirements || typeof drvRequirements !== 'object') return null;
  const root = drvRequirements as Record<string, unknown>;
  const inner = (root.drvRequirements ?? root.data ?? root) as unknown;
  return inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : null;
}

function resolvePrimaryField(primaryTypes: DrvPrimaryType[] | undefined, nutrientIdCandidates: string[]): string | null {
  const wanted = new Set(nutrientIdCandidates.map(normalizeDrvLookupKey));
  const primaryType = primaryTypes?.find((item) => wanted.has(normalizeDrvLookupKey(item.nutrientId)));
  const explicitField = String(primaryType?.primaryField || '').trim();
  if (explicitField) return explicitField;

  const rawType = String(primaryType?.primaryType || primaryType?.primaryRawType || '').trim().toLowerCase();
  if (!rawType) return null;
  if (rawType === 'rda') return 'rda';
  if (rawType === 'ai') return 'ai';
  if (rawType === 'ear') return 'ear';
  if (rawType === 'lrni') return 'lrni';
  if (rawType === 'ul') return 'ul';
  if (rawType.includes('amdr')) return 'amdr';
  return null;
}

function normalizePercentEnergyUnit(unit: string): string {
  const normalized = unit.trim();
  if (normalized === '%E' || normalized === '%e') return '%';
  return normalized;
}

function formatAmdrPercentRecommended(requirement: Record<string, unknown> | undefined): string | null {
  const low = toPositiveNumber(requirement?.amdrLow);
  const high = toPositiveNumber(requirement?.amdrHigh);
  if (low == null || high == null) return null;
  return `${formatMetric(low, 0)}-${formatMetric(high, 0)}%`;
}

function formatUlPercentRecommended(requirement: Record<string, unknown> | undefined): string | null {
  const ul = toPositiveNumber(requirement?.ul);
  if (ul == null) return null;
  return `≤${formatMetric(ul, 0)}%`;
}

function formatMacroPercentSuggestion(
  requirement: Record<string, unknown> | undefined,
  options?: { ulFallback?: boolean },
): string | null {
  const amdrDisplay = formatAmdrPercentRecommended(requirement);
  if (amdrDisplay) return amdrDisplay;

  const amdr = toPositiveNumber(requirement?.amdr);
  if (amdr != null) return `${formatMetric(amdr, 0)}%`;

  if (options?.ulFallback) {
    return formatUlPercentRecommended(requirement);
  }

  return null;
}

function resolveAmdrGramRange(requirement: Record<string, unknown> | undefined): { low: number; high: number } | null {
  const low = toPositiveNumber(requirement?.amdrLowGrams);
  const high = toPositiveNumber(requirement?.amdrHighGrams);
  if (low == null || high == null || low > high) return null;
  return { low, high };
}

function formatGramRangeForSuggestion(range: { low: number; high: number }, unit: string): string {
  const lowDecimals = getDecimals(unit, range.low);
  const highDecimals = getDecimals(unit, range.high);
  return `${formatMetric(range.low, lowDecimals)}-${formatMetric(range.high, highDecimals)}`;
}

function formatGramValueForSuggestion(value: number, unit: string): string {
  return formatMetric(value, getDecimals(unit, value));
}

type SuggestedRdaMacroResolved = Pick<
  Parameters<typeof buildSuggestedRdaRow>[0],
  'unit' | 'recommended' | 'recommendedDisplay' | 'gramRange' | 'preferLower' | 'fields'
>;

/**
 * Macro suggested-RDA priority mirrors the percentage display: AMDR range → AMDR single → RDA/AI fallback.
 */
function resolveSuggestedRdaMacroRow(
  requirement: Record<string, unknown> | undefined,
  fallbackTarget: { recommended: number; unit: string },
  options?: { ulFallback?: boolean; aiFallback?: boolean; gramUnit?: string },
): SuggestedRdaMacroResolved {
  const unit = normalizeDrvUnit(requirement?.unit, fallbackTarget.unit);
  const gramUnit = options?.gramUnit ?? 'g/day';
  const fields = {
    percentage: formatMacroPercentSuggestion(requirement, { ulFallback: options?.ulFallback }) ?? '--',
  };

  if (options?.ulFallback) {
    const ulGrams = toPositiveNumber(requirement?.ulGrams);
    const ulDisplay = formatUlPercentRecommended(requirement);
    if (ulGrams != null || ulDisplay) {
      if (ulGrams != null) {
        return {
          unit: gramUnit,
          recommended: ulGrams,
          recommendedDisplay: `≤${formatGramValueForSuggestion(ulGrams, gramUnit)}`,
          preferLower: true,
          fields,
        };
      }

      return {
        unit: normalizePercentEnergyUnit(unit),
        recommendedDisplay: ulDisplay,
        preferLower: true,
        fields,
      };
    }
  }

  const amdrDisplay = formatAmdrPercentRecommended(requirement);
  if (amdrDisplay != null) {
    const gramRange = resolveAmdrGramRange(requirement);
    if (gramRange) {
      return {
        unit: gramUnit,
        recommendedDisplay: formatGramRangeForSuggestion(gramRange, gramUnit),
        gramRange,
        fields,
      };
    }

    return {
      unit: normalizePercentEnergyUnit(unit),
      recommendedDisplay: amdrDisplay,
      fields,
    };
  }

  const amdr = toPositiveNumber(requirement?.amdr);
  if (amdr != null) {
    const amdrGrams = toPositiveNumber(requirement?.amdrGrams);
    if (amdrGrams != null) {
      return {
        unit: gramUnit,
        recommended: amdrGrams,
        recommendedDisplay: formatGramValueForSuggestion(amdrGrams, gramUnit),
        fields,
      };
    }

    return {
      unit: normalizePercentEnergyUnit(unit),
      recommendedDisplay: `${formatMetric(amdr, 0)}%`,
      fields,
    };
  }

  const rda = toPositiveNumber(requirement?.rda);
  if (rda != null) {
    return { unit, recommended: rda, fields };
  }

  if (options?.aiFallback) {
    const ai = toPositiveNumber(requirement?.ai);
    if (ai != null) {
      return { unit, recommended: ai, fields };
    }
  }

  return {
    unit: fallbackTarget.unit,
    recommended: Number.isFinite(fallbackTarget.recommended) ? fallbackTarget.recommended : null,
    fields,
  };
}

function resolveDrvDisplayTarget(
  drvRequirements: unknown,
  primaryTypes: DrvPrimaryType[] | undefined,
  nutrientIdCandidates: string[],
  fallbackUnit: string,
): { recommended: number; unit: string } {
  const requirement = findDrvRequirementByNutrientIds(drvRequirements, nutrientIdCandidates);
  const unit = normalizeDrvUnit(requirement?.unit, fallbackUnit);

  if (!requirement) {
    return {
      recommended: resolveDrvRecommendedValue(drvRequirements, primaryTypes, nutrientIdCandidates, Number.NaN),
      unit,
    };
  }

  const rda = toPositiveNumber(requirement.rda);
  if (rda != null) return { recommended: rda, unit };

  const amdr = toPositiveNumber(requirement.amdr);
  if (amdr != null) return { recommended: amdr, unit };

  const primaryField = resolvePrimaryField(primaryTypes, nutrientIdCandidates);
  const fields =
    primaryField && !primaryField.includes('amdr') ? [primaryField] : [];
  const fallbackFields = ['ai', 'ear', 'lrni', 'ul'];

  for (const field of [...fields, ...fallbackFields]) {
    const value = toPositiveNumber(requirement[field]);
    if (value != null) return { recommended: value, unit };
  }

  return { recommended: Number.NaN, unit };
}

function gramFieldForPrimaryField(primaryField: string | null): string | null {
  const normalized = String(primaryField ?? '').trim();
  if (!normalized || normalized.toLowerCase().includes('amdr')) return null;
  return `${normalized}Grams`;
}

function resolveNutritionReportGramTarget(
  drvRequirements: unknown,
  primaryTypes: DrvPrimaryType[] | undefined,
  nutrientIdCandidates: string[],
  fallback: number | null,
): number | null {
  const requirement = findDrvRequirementByNutrientIds(drvRequirements, nutrientIdCandidates);
  if (!requirement) return fallback;

  const primaryField = resolvePrimaryField(primaryTypes, nutrientIdCandidates);
  const fields: string[] = [];
  const primaryGramField = gramFieldForPrimaryField(primaryField);
  if (primaryGramField) fields.push(primaryGramField);
  if (primaryField && !primaryField.toLowerCase().includes('amdr')) fields.push(primaryField);
  fields.push('rdaGrams', 'aiGrams', 'ulGrams', 'amdrGrams', 'rda', 'ai', 'ul', 'amdr');

  for (const field of fields) {
    const value = toPositiveNumber(requirement[field]);
    if (value != null) return value;
  }

  return fallback;
}

/** Client DRV AMDR band for Limits: prefers `amdrLowGrams`–`amdrHighGrams`, else `amdrLow`–`amdrHigh` %E. */
function formatMacroAmdrLimits(
  drvRequirements: unknown,
  nutrientIdCandidates: readonly string[],
  unit: string,
): string | undefined {
  const row = findDrvRequirementByNutrientIds(drvRequirements, [...nutrientIdCandidates]);
  if (!row) return undefined;
  const lowG = safeNumber(row.amdrLowGrams, Number.NaN);
  const highG = safeNumber(row.amdrHighGrams, Number.NaN);
  if (Number.isFinite(lowG) && Number.isFinite(highG) && highG > 0 && lowG >= 0 && lowG <= highG) {
    return `${formatMetric(lowG, getDecimals(unit, lowG))}-${formatMetric(highG, getDecimals(unit, highG))}${unit}`;
  }
  const lowP = safeNumber(row.amdrLow, Number.NaN);
  const highP = safeNumber(row.amdrHigh, Number.NaN);
  if (Number.isFinite(lowP) && Number.isFinite(highP) && highP > 0 && lowP >= 0 && lowP <= highP) {
    return `${formatMetric(lowP, 0)}-${formatMetric(highP, 0)}%E`;
  }
  return undefined;
}

function formatWithUnit(value: number, unit: string): string {
  return `${formatMetric(value, getDecimals(unit, value))}${unit}`;
}

function formatClinicalTargetRangeLimit(
  range: MealPlanClinicalTargetRange | undefined,
  fallbackUnit: string,
): string | undefined {
  if (!range) return undefined;
  const unit = String(range.unit || fallbackUnit);
  const min = toPositiveNumber(range.min);
  const max = toPositiveNumber(range.max);
  if (min == null && max == null) return undefined;
  if (min != null && max != null) {
    if (Math.round(min * 1000) === Math.round(max * 1000)) {
      return formatWithUnit(max, unit);
    }
    return `${formatMetric(min, getDecimals(unit, min))}-${formatMetric(max, getDecimals(unit, max))}${unit}`;
  }
  if (max != null) return `≤${formatWithUnit(max, unit)}`;
  return `≥${formatWithUnit(min as number, unit)}`;
}

function formatForTableValue(value: number, unit: string): string {
  return formatMetric(value, getDecimals(unit, value));
}

function getReportRowTone(progress: number, preferLower: boolean): RowTone {
  if (preferLower) {
    if (progress <= 100) return 'met';
    if (progress <= 125) return 'borderline';
    return 'excess';
  }

  return getSuggestedRdaToneFromPercent(progress, false);
}

function buildReportRow(args: {
  id: string;
  label: string;
  intake: number;
  /** When set (e.g. em dash), shown in the Intake column instead of formatting `intake`. */
  intakeDisplay?: string;
  unit: string;
  target?: number | null;
  limits?: string;
  preferLower?: boolean;
  nutrientApiKey?: string;
  /** Prefer the backend DRV status when its target semantics are known. */
  tone?: RowTone;
  clinicalLimit?: MealPlanClinicalLimit;
  actualEnergy?: number;
  kcalPerGram?: number;
}): NutritionReportRow {
  const targetValue = Number.isFinite(args.target) && (args.target as number) > 0 ? (args.target as number) : null;
  const apiKey =
    args.nutrientApiKey != null && String(args.nutrientApiKey).trim()
      ? String(args.nutrientApiKey).trim().toLowerCase()
      : undefined;

  const intakeLabel = args.intakeDisplay ?? formatWithUnit(args.intake, args.unit);
  const numericIntake =
    args.intakeDisplay != null
      ? null
      : Number.isFinite(args.intake)
        ? args.intake
        : null;
  const limitStatus = numericIntake != null && args.clinicalLimit
    ? clinicalLimitStatus({
        limit: args.clinicalLimit,
        intake: numericIntake,
        actualEnergy: args.actualEnergy ?? 0,
        kcalPerGram: args.kcalPerGram,
      })
    : null;

  if (targetValue == null) {
    return {
      id: args.id,
      label: args.label,
      intake: intakeLabel,
      target: 'N/A',
      limits: args.limits ?? '--',
      progress: limitStatus?.progress ?? null,
      tone: args.tone ?? limitStatus?.tone ?? 'na',
      ...(limitStatus?.explanation ? { limitCalculationExplanation: limitStatus.explanation } : {}),
      ...(apiKey ? { nutrientApiKey: apiKey } : {}),
    };
  }

  if (numericIntake == null) {
    return {
      id: args.id,
      label: args.label,
      intake: intakeLabel,
      target: args.clinicalLimit?.kind === 'minimum' ? `≥${formatWithUnit(targetValue, args.unit)}` : formatWithUnit(targetValue, args.unit),
      limits: args.limits ?? '--',
      progress: null,
      tone: 'na',
      ...(apiKey ? { nutrientApiKey: apiKey } : {}),
    };
  }

  const targetProgress = (numericIntake / Math.max(1, targetValue)) * 100;
  const progress = limitStatus?.progress ?? targetProgress;
  const tone = args.tone ?? limitStatus?.tone ?? getReportRowTone(progress, Boolean(args.preferLower));

  return {
    id: args.id,
    label: args.label,
    intake: formatWithUnit(numericIntake, args.unit),
    target: args.clinicalLimit?.kind === 'minimum' ? `≥${formatWithUnit(targetValue, args.unit)}` : formatWithUnit(targetValue, args.unit),
    limits: args.limits ?? '--',
    progress,
    tone,
    ...(limitStatus?.explanation ? { limitCalculationExplanation: limitStatus.explanation } : {}),
    ...(apiKey ? { nutrientApiKey: apiKey } : {}),
  };
}

function buildSuggestedRdaRow(args: {
  id: string;
  label: string;
  unit: string;
  current?: number | null;
  recommended?: number | null;
  /** When set, shown in Recommended RDA instead of formatting `recommended`. */
  recommendedDisplay?: string | null;
  /** AMDR gram band for status / % Met (carbs, total fat). */
  gramRange?: { low: number; high: number } | null;
  preferLower?: boolean;
  fields?: Record<string, RowFieldValue>;
  targetCalculationExplanation?: string;
}): NutritionReportRow {
  const recommendedValue = Number.isFinite(args.recommended) && (args.recommended as number) > 0 ? (args.recommended as number) : null;
  const currentValue = Number.isFinite(args.current) && (args.current as number) > 0 ? (args.current as number) : null;
  const recommendedLabel =
    args.recommendedDisplay != null && String(args.recommendedDisplay).trim()
      ? String(args.recommendedDisplay).trim()
      : recommendedValue == null
        ? '--'
        : formatForTableValue(recommendedValue, args.unit);
  const gramRange = args.gramRange ?? null;
  const hasGramRange = gramRange != null && gramRange.low > 0 && gramRange.high > 0;
  const canEvaluate = currentValue != null && (hasGramRange || recommendedValue != null);

  if (!canEvaluate) {
    return {
      id: args.id,
      label: args.label,
      unit: args.unit,
      recommended: recommendedLabel,
      currentPlan: currentValue == null ? '--' : formatForTableValue(currentValue, args.unit),
      fields: args.fields,
      targetCalculationExplanation: args.targetCalculationExplanation,
      progress: null,
      tone: 'na',
    };
  }

  const progress = hasGramRange
    ? getSuggestedRdaProgressForGramRange(currentValue as number, gramRange.low, gramRange.high)
    : (currentValue as number / Math.max(1, recommendedValue as number)) * 100;
  const tone = hasGramRange
    ? getSuggestedRdaRowToneForGramRange(currentValue as number, gramRange.low, gramRange.high)
    : getSuggestedRdaRowTone(currentValue as number, recommendedValue as number, Boolean(args.preferLower));

  return {
    id: args.id,
    label: args.label,
    unit: args.unit,
    recommended: recommendedLabel,
    currentPlan: formatForTableValue(currentValue as number, args.unit),
    fields: args.fields,
    targetCalculationExplanation: args.targetCalculationExplanation,
    progress,
    tone,
  };
}

function fallbackDisplayNameFromNutrientId(nutrientId: string): string {
  return nutrientId
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => {
      if (/^b\d+$/i.test(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function getDrvMicronutrientRows(rawReqs: unknown, groupKeys: string[]): Record<string, unknown>[] {
  const root = unwrapDrvRequirementRoot(rawReqs);
  const micronutrients = root?.micronutrients;
  if (!micronutrients || typeof micronutrients !== 'object') return [];

  const groups = micronutrients as Record<string, unknown>;
  return groupKeys.flatMap((groupKey) => {
    const rows = groups[groupKey];
    return Array.isArray(rows) ? rows.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object') : [];
  });
}

function buildSuggestedRdaMicronutrientRows(
  rawReqs: unknown,
  primaryTypes: DrvPrimaryType[] | undefined,
  groupKeys: string[],
): Parameters<typeof buildSuggestedRdaRow>[0][] {
  const seen = new Set<string>();

  return getDrvMicronutrientRows(rawReqs, groupKeys).flatMap((requirement) => {
    const nutrientId = String(requirement.nutrientId ?? '').trim();
    if (!nutrientId) return [];

    const normalizedId = normalizeNutrientIdKey(nutrientId);
    if (seen.has(normalizedId)) return [];
    seen.add(normalizedId);

    const fallbackUnit = normalizeDrvUnit(requirement.unit, '');
    const target = resolveDrvDisplayTarget(rawReqs, primaryTypes, [nutrientId], fallbackUnit);
    const displayName = String(requirement.displayName ?? '').trim() || fallbackDisplayNameFromNutrientId(nutrientId);

    return [{
      id: normalizedId.replace(/_/g, '-'),
      label: displayName,
      unit: target.unit,
      recommended: target.recommended,
      targetCalculationExplanation: typeof requirement.targetCalculationExplanation === 'string'
        ? requirement.targetCalculationExplanation
        : undefined,
    }];
  });
}

function mealPlanDetailGram(
  useMealPlanApi: boolean,
  detail: MealPlanNutritionReportDetail | undefined,
  key: keyof MealPlanNutritionReportDetail,
  fallbackGrams: number,
  missingLabel: string,
  missingLabels: string[],
): { grams: number; intakeDisplay?: string } {
  if (!useMealPlanApi || !detail) return { grams: fallbackGrams };
  const v = detail[key];
  if (typeof v === 'number' && Number.isFinite(v)) return { grams: v };
  if (!missingLabels.includes(missingLabel)) missingLabels.push(missingLabel);
  return { grams: 0, intakeDisplay: '—' };
}

function buildNutritionReportSections(
  analysisData: AnalysisData,
  microsView?: NutritionalAnalysisMicrosView | null,
  drvRequirements?: unknown,
  primaryTypes?: DrvPrimaryType[],
): { sections: NutritionSection[]; mealPlanReportMissingLabels: string[] } {
  const detail = analysisData.mealPlanNutritionReportDetail;
  const useMealPlanApi = detail !== undefined;
  const mealPlanReportMissingLabels: string[] = [];
  const clinicalRanges = analysisData.mealPlanClinicalTargetRanges ?? {};
  const clinicalLimits = analysisData.mealPlanClinicalLimits ?? {};

  const carbAmdrLimits = formatStructuredClinicalLimit(clinicalLimits.carbs)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.carbs, 'g')
    ?? formatMacroAmdrLimits(drvRequirements, ['carbohydrate', 'carbohydrates'], 'g');
  const proteinAmdrLimits = formatStructuredClinicalLimit(clinicalLimits.protein)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.protein, 'g')
    ?? formatMacroAmdrLimits(drvRequirements, ['protein'], 'g');
  const fatAmdrLimits = formatStructuredClinicalLimit(clinicalLimits.fat)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.fat, 'g')
    ?? formatMacroAmdrLimits(drvRequirements, ['total_fat', 'fat', 'lipid'], 'g');
  const fibreClinicalLimits = formatStructuredClinicalLimit(clinicalLimits.fibre)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.fibre, 'g');
  const saturatedFatClinicalLimits = formatStructuredClinicalLimit(clinicalLimits.saturatedFat)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.saturatedFat, 'g');
  const freeSugarsClinicalLimits = formatStructuredClinicalLimit(clinicalLimits.freeSugars)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.freeSugars, 'g');
  const sodiumClinicalLimits = formatStructuredClinicalLimit(clinicalLimits.sodium)
    ?? formatClinicalTargetRangeLimit(clinicalRanges.sodium, 'mg');
  const lipidKpiTarget = (kpi: ReturnType<typeof getKpiByLabel>): number | null => {
    const t = kpi?.target;
    return Number.isFinite(t) && (t as number) > 0 ? (t as number) : null;
  };

  const freeSugarKpiTarget = getKpiByLabel(analysisData, 'Free Sugars')?.target;
  const freeSugarTarget =
    freeSugarKpiTarget != null && Number.isFinite(freeSugarKpiTarget) && freeSugarKpiTarget > 0
      ? freeSugarKpiTarget
      : resolveNutritionReportGramTarget(
        drvRequirements,
        primaryTypes,
        ['free_sugars', 'added_sugars', 'sugar'],
        null,
      );
  const totalSugarTarget = resolveNutritionReportGramTarget(
    drvRequirements,
    primaryTypes,
    ['sugar', 'sugars', 'total_sugar', 'total_sugars'],
    null,
  );
  const saturatedFatKpiTarget = lipidKpiTarget(getKpiByLabel(analysisData, 'Saturated Fat'));
  const saturatedFatTarget =
    saturatedFatKpiTarget
    ?? resolveNutritionReportGramTarget(drvRequirements, primaryTypes, ['saturated_fat'], null);
  const alcoholKpi = getKpiByLabel(analysisData, 'Alcohol');
  const alcoholIntake = alcoholKpi?.value ?? 0;
  const alcoholTarget =
    lipidKpiTarget(alcoholKpi)
    ?? resolveNutritionReportGramTarget(drvRequirements, primaryTypes, ['alcohol'], null);

  const transFatKpi = getKpiByLabel(analysisData, 'Trans Fat');
  const fatSubtype = analysisData.fatSubtypeGrams;
  const transFromGrams =
    fatSubtype != null && typeof fatSubtype.trans === 'number' && Number.isFinite(fatSubtype.trans)
      ? fatSubtype.trans
      : null;
  const mufaFromGrams =
    fatSubtype != null && typeof fatSubtype.monounsaturated === 'number' && Number.isFinite(fatSubtype.monounsaturated)
      ? fatSubtype.monounsaturated
      : null;
  const pufaFromGrams =
    fatSubtype != null && typeof fatSubtype.polyunsaturated === 'number' && Number.isFinite(fatSubtype.polyunsaturated)
      ? fatSubtype.polyunsaturated
      : null;

  let transFatIntake = 0;
  let transFatIntakeDisplay: string | undefined;
  if (useMealPlanApi && detail) {
    if (detail.trans_fat != null && Number.isFinite(detail.trans_fat)) {
      transFatIntake = detail.trans_fat;
    } else if (transFromGrams != null) {
      transFatIntake = transFromGrams;
    } else {
      transFatIntakeDisplay = '—';
      if (!mealPlanReportMissingLabels.includes('Trans-fat')) mealPlanReportMissingLabels.push('Trans-fat');
    }
  } else if (transFatKpi != null && Number.isFinite(transFatKpi.value)) {
    transFatIntake = transFatKpi.value;
  } else if (transFromGrams != null) {
    transFatIntake = transFromGrams;
  } else {
    transFatIntakeDisplay = '—';
  }
  const transFatTarget = lipidKpiTarget(transFatKpi);

  const mufaKpi = getKpiByLabel(analysisData, 'Monounsaturated Fat');
  let mufaIntake = 0;
  let mufaIntakeDisplay: string | undefined;
  if (useMealPlanApi && detail) {
    if (detail.mufa != null && Number.isFinite(detail.mufa)) {
      mufaIntake = detail.mufa;
    } else if (mufaFromGrams != null) {
      mufaIntake = mufaFromGrams;
    } else {
      mufaIntakeDisplay = '—';
      if (!mealPlanReportMissingLabels.includes('Monounsaturated fat')) mealPlanReportMissingLabels.push('Monounsaturated fat');
    }
  } else if (mufaKpi != null && Number.isFinite(mufaKpi.value)) {
    mufaIntake = mufaKpi.value;
  } else if (mufaFromGrams != null) {
    mufaIntake = mufaFromGrams;
  } else {
    mufaIntakeDisplay = '—';
  }
  const mufaKpiTarget = lipidKpiTarget(mufaKpi);
  const mufaTarget = resolveNutritionReportGramTarget(
    drvRequirements,
    primaryTypes,
    ['monounsaturated_fat', 'mufa', 'monounsaturated', 'monounsaturatedfat'],
    mufaKpiTarget,
  );

  const pufaKpi = getKpiByLabel(analysisData, 'Polyunsaturated Fat');
  let pufaIntake = 0;
  let pufaIntakeDisplay: string | undefined;
  if (useMealPlanApi && detail) {
    if (detail.pufa != null && Number.isFinite(detail.pufa)) {
      pufaIntake = detail.pufa;
    } else if (pufaFromGrams != null) {
      pufaIntake = pufaFromGrams;
    } else {
      pufaIntakeDisplay = '—';
      if (!mealPlanReportMissingLabels.includes('Polyunsaturated fat')) mealPlanReportMissingLabels.push('Polyunsaturated fat');
    }
  } else if (pufaKpi != null && Number.isFinite(pufaKpi.value)) {
    pufaIntake = pufaKpi.value;
  } else if (pufaFromGrams != null) {
    pufaIntake = pufaFromGrams;
  } else {
    pufaIntakeDisplay = '—';
  }
  const pufaKpiTarget = lipidKpiTarget(pufaKpi);
  const pufaTarget = resolveNutritionReportGramTarget(
    drvRequirements,
    primaryTypes,
    ['polyunsaturated_fat', 'pufa', 'polyunsaturated', 'polyunsaturatedfat'],
    pufaKpiTarget,
  );
  const cholesterolKpi = getKpiByLabel(analysisData, 'Cholesterol');
  const cholesterolMicro = findMicrosItemByNutrientIds(microsView, ['cholesterol']);

  let cholesterolIntake = cholesterolKpi != null && Number.isFinite(cholesterolKpi.value) ? cholesterolKpi.value : 0;
  let cholesterolIntakeDisplay: string | undefined;

  const cholesterolIntakeFromMicros = cholesterolMicro?.intakeValue;
  if (cholesterolIntakeFromMicros != null && Number.isFinite(cholesterolIntakeFromMicros) && cholesterolIntakeFromMicros >= 0) {
    cholesterolIntake = cholesterolIntakeFromMicros;
    cholesterolIntakeDisplay = undefined;
  } else if (useMealPlanApi && detail) {
    if (detail.cholesterol != null && Number.isFinite(detail.cholesterol)) {
      cholesterolIntake = detail.cholesterol;
    } else {
      cholesterolIntake = 0;
      if (!mealPlanReportMissingLabels.includes('Cholesterol')) mealPlanReportMissingLabels.push('Cholesterol');
    }
  }

  const cholesterolTarget = microsTargetGramsFromApi(cholesterolMicro);

  const starchG = mealPlanDetailGram(useMealPlanApi, detail, 'starch', 0, 'Starch', mealPlanReportMissingLabels);
  const oligoG = mealPlanDetailGram(
    useMealPlanApi,
    detail,
    'oligosaccharide',
    0,
    'Oligosaccharide',
    mealPlanReportMissingLabels,
  );
  const sugarTotalG = mealPlanDetailGram(
    useMealPlanApi,
    detail,
    'sugar',
    0,
    'Sugars (total)',
    mealPlanReportMissingLabels,
  );
  const freeSugarsRow = mealPlanDetailGram(
    useMealPlanApi,
    detail,
    'free_sugars',
    analysisData.freeSugar,
    'Free sugars',
    mealPlanReportMissingLabels,
  );
  const omega3G = mealPlanDetailGram(useMealPlanApi, detail, 'omega_3', 0, 'Omega-3', mealPlanReportMissingLabels);
  const omega6G = mealPlanDetailGram(useMealPlanApi, detail, 'omega_6', 0, 'Omega-6', mealPlanReportMissingLabels);
  const omega3Micro = findMicrosItemByNutrientIds(microsView, ['omega_3', 'ala']);
  const omega6Micro = findMicrosItemByNutrientIds(microsView, ['omega_6', 'la']);
  const omega3FromApi = omegaIntakeFromMicrosOrDetail(omega3Micro, omega3G);
  const omega6FromApi = omegaIntakeFromMicrosOrDetail(omega6Micro, omega6G);
  const omega3Target = microsTargetGramsFromApi(omega3Micro);
  const omega6Target = microsTargetGramsFromApi(omega6Micro);
  type NutritionReportRowConfig = Parameters<typeof buildReportRow>[0];
  type NutritionReportSectionConfig = {
    id: string;
    title: string;
    accent: string;
    headerBg: string;
    columns?: SuggestedRdaColumn[];
    rows: NutritionReportRowConfig[];
  };

  // Add new Analysis tab sections/fields by extending this config array.
  const sectionConfigs: NutritionReportSectionConfig[] = [
    {
      id: 'energy',
      title: 'Energy',
      accent: '#4E5AC8',
      headerBg: '#EEF0FF',
      rows: [
        {
          id: 'energy-row',
          label: 'Energy',
          intake: analysisData.energy,
          target: analysisData.energyTarget,
          unit: 'kcal',
          limits: '--',
          nutrientApiKey: 'energy',
        },
      ],
    },
    {
      id: 'macronutrients',
      title: 'Macronutrients',
      accent: '#0B89B7',
      headerBg: '#EAF8FD',
      rows: [
        {
          id: 'carbs',
          label: 'Carbohydrate',
          intake: analysisData.carbs,
          target: analysisData.carbsTarget,
          unit: 'g',
          ...(carbAmdrLimits ? { limits: carbAmdrLimits } : {}),
          clinicalLimit: clinicalLimits.carbs,
          actualEnergy: analysisData.energy,
          kcalPerGram: 4,
          nutrientApiKey: 'carbohydrate',
        },
        {
          id: 'protein',
          label: 'Protein',
          intake: analysisData.protein,
          target: analysisData.proteinTarget,
          unit: 'g',
          ...(proteinAmdrLimits ? { limits: proteinAmdrLimits } : {}),
          clinicalLimit: clinicalLimits.protein,
          actualEnergy: analysisData.energy,
          kcalPerGram: 4,
          nutrientApiKey: 'protein',
        },
        {
          id: 'fat',
          label: 'Fat',
          intake: analysisData.fat,
          target: analysisData.fatTarget,
          unit: 'g',
          ...(fatAmdrLimits ? { limits: fatAmdrLimits } : {}),
          clinicalLimit: clinicalLimits.fat,
          actualEnergy: analysisData.energy,
          kcalPerGram: 9,
          nutrientApiKey: 'total_fat',
        },
        {
          id: 'water',
          label: 'Water',
          intake: analysisData.water,
          target: analysisData.waterTarget,
          unit: 'L',
          nutrientApiKey: 'water',
        },
        {
          id: 'alcohol',
          label: 'Alcohol',
          intake: alcoholIntake,
          target: alcoholTarget,
          unit: 'g',
          preferLower: true,
          nutrientApiKey: 'alcohol',
        },
      ],
    },
    {
      id: 'carb-detail',
      title: 'Carbohydrate Detail',
      accent: '#0B89B7',
      headerBg: '#EAF8FD',
      rows: [
        {
          id: 'starch',
          label: 'Starch',
          intake: starchG.grams,
          intakeDisplay: starchG.intakeDisplay,
          unit: 'g',
          target: null,
          limits: '--',
          nutrientApiKey: 'starch',
        },
        {
          id: 'oligosaccharide',
          label: 'Oligosaccharide',
          intake: oligoG.grams,
          intakeDisplay: oligoG.intakeDisplay,
          unit: 'g',
          target: null,
          limits: '--',
          nutrientApiKey: 'oligosaccharide',
        },
        {
          id: 'nsp-fibre',
          label: 'Fibre',
          intake: analysisData.fibre,
          target: analysisData.fibreTarget,
          unit: 'g',
          ...(fibreClinicalLimits ? { limits: fibreClinicalLimits } : {}),
          clinicalLimit: clinicalLimits.fibre ?? { kind: 'minimum', unit: 'g', min: analysisData.fibreTarget },
          actualEnergy: analysisData.energy,
          nutrientApiKey: 'dietary_fibre',
        },
        {
          id: 'sugars',
          label: 'Sugars (total)',
          intake: sugarTotalG.grams,
          intakeDisplay: sugarTotalG.intakeDisplay,
          target: totalSugarTarget,
          unit: 'g',
          preferLower: true,
          nutrientApiKey: 'sugar',
        },
        {
          id: 'free-sugars',
          label: 'Free sugars',
          intake: freeSugarsRow.grams,
          intakeDisplay: freeSugarsRow.intakeDisplay,
          target: freeSugarTarget,
          unit: 'g',
          ...(freeSugarsClinicalLimits ? { limits: freeSugarsClinicalLimits } : {}),
          clinicalLimit: clinicalLimits.freeSugars,
          actualEnergy: analysisData.energy,
          kcalPerGram: 4,
          preferLower: true,
          nutrientApiKey: 'free_sugars',
        },
      ],
    },
    {
      id: 'lipid-components',
      title: 'Lipid Components',
      accent: '#7A38CC',
      headerBg: '#F2ECFF',
      rows: [
        {
          id: 'sat-fat',
          label: 'Saturated Fat',
          intake: analysisData.saturatedFat,
          target: saturatedFatTarget,
          unit: 'g',
          ...(saturatedFatClinicalLimits ? { limits: saturatedFatClinicalLimits } : {}),
          clinicalLimit: clinicalLimits.saturatedFat,
          actualEnergy: analysisData.energy,
          kcalPerGram: 9,
          preferLower: true,
          nutrientApiKey: 'saturated_fat',
        },
        {
          id: 'mono-fat',
          label: 'Monounsaturated fat',
          intake: mufaIntake,
          intakeDisplay: mufaIntakeDisplay,
          target: mufaTarget,
          unit: 'g',
          nutrientApiKey: 'monounsaturated_fat',
        },
        {
          id: 'poly-fat',
          label: 'Polyunsaturated fat',
          intake: pufaIntake,
          intakeDisplay: pufaIntakeDisplay,
          target: pufaTarget,
          unit: 'g',
          nutrientApiKey: 'polyunsaturated_fat',
        },
        {
          id: 'omega-3',
          label: 'Omega-3',
          intake: omega3FromApi.intake,
          intakeDisplay: omega3FromApi.intakeDisplay,
          target: omega3Target,
          unit: 'g',
          nutrientApiKey: 'omega_3',
          tone: rowToneFromMicronutrientStatus(omega3Micro?.status),
        },
        {
          id: 'omega-6',
          label: 'Omega-6',
          intake: omega6FromApi.intake,
          intakeDisplay: omega6FromApi.intakeDisplay,
          target: omega6Target,
          unit: 'g',
          nutrientApiKey: 'omega_6',
          tone: rowToneFromMicronutrientStatus(omega6Micro?.status),
        },
        {
          id: 'trans-fat',
          label: 'Trans fat',
          intake: transFatIntake,
          intakeDisplay: transFatIntakeDisplay,
          target: transFatTarget,
          unit: 'g',
          preferLower: true,
          nutrientApiKey: 'trans_fat',
        },
        {
          id: 'cholesterol',
          label: 'Cholesterol',
          intake: cholesterolIntake,
          intakeDisplay: cholesterolIntakeDisplay,
          target: cholesterolTarget,
          unit: 'mg',
          preferLower: true,
          nutrientApiKey: 'cholesterol',
        },
      ],
    },
  ];

  const sections = sectionConfigs.map((sectionConfig) => ({
    id: sectionConfig.id,
    title: sectionConfig.title,
    accent: sectionConfig.accent,
    headerBg: sectionConfig.headerBg,
    columns: sectionConfig.columns ?? reportColumns,
    rows: sectionConfig.rows.map((rowConfig) => buildReportRow(rowConfig)),
  }));

  if (microsView?.byStatus?.all?.length) {
    const microsById = new Map(microsView.byStatus.all.map((m) => [m.nutrientId.trim().toLowerCase(), m]));
    const buildRows = (rows: NutritionalAnalysisMicrosView['byStatus']['all'], prefix: string) =>
      rows
        .map((m) => {
          const unit = m.intakeUnit || 'mg';
          const intake = m.intakeValue ?? 0;
          const target = m.rdaValue ?? null;
          const nutrientApiKey = m.nutrientId.trim().toLowerCase().replace(/\s+/g, '_');
          const limits = nutrientApiKey === 'sodium' && sodiumClinicalLimits
            ? sodiumClinicalLimits
            : formatMicroNutrientLimits(m, unit);
          const sodiumLimitStatus = nutrientApiKey === 'sodium' && clinicalLimits.sodium
            ? clinicalLimitStatus({
                limit: clinicalLimits.sodium,
                intake,
                actualEnergy: analysisData.energy,
              })
            : null;
          // Match Clinical Insight status; sodium caps have no 10% borderline allowance.
          const tone: RowTone = m.status === 'adequate'
              ? 'met'
              : m.status === 'excess'
                ? 'excess'
                : m.status === 'toxic'
                  ? 'toxic'
                  : m.status === 'low'
                    ? 'deficient'
                    : 'na';
          return {
            id: `${prefix}-${m.nutrientId}`,
            label: m.relatedConditions?.length ? `${m.displayName} · ${m.relatedConditions.join(', ')}` : m.displayName,
            intake: m.priorityOnly && target == null ? '—' : formatWithUnit(intake, unit),
            target: target != null ? `${m.nutrientId === 'dietary_fibre' ? '≥' : ['sodium', 'free_sugars'].includes(m.nutrientId) ? '≤' : ''}${formatWithUnit(target, unit)}` : m.clinicalPriority ? 'Priority' : 'N/A',
            limits,
            progress: sodiumLimitStatus?.progress
              ?? (Number.isFinite(m.percentOfTarget) ? m.percentOfTarget : null),
            tone,
            ...(sodiumLimitStatus?.explanation
              ? { limitCalculationExplanation: sodiumLimitStatus.explanation }
              : {}),
            nutrientApiKey,
          } satisfies NutritionReportRow;
        });

    const representedClinicalIds = new Set<string>();
    for (const section of sections) {
      section.rows = section.rows.map((row) => {
        const aliases: Record<string, string> = { monounsaturated_fat: 'mufa', polyunsaturated_fat: 'pufa' };
        const id = aliases[row.nutrientApiKey ?? ''] ?? row.nutrientApiKey;
        const micro = id ? microsById.get(id) : undefined;
        if (!micro?.clinicalPriority) return row;
        representedClinicalIds.add(micro.nutrientId);
        const clinicalRow = buildRows([micro], 'clinical')[0];
        return { ...row, ...clinicalRow, id: row.id, limits: row.limits, nutrientApiKey: row.nutrientApiKey };
      });
    }

    const buildRowsByIds = (ids: readonly string[], prefix: string) =>
      ids
        .map((id) => microsById.get(id))
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
        .flatMap((m) => buildRows([m], prefix));

    const vitaminRows = buildRowsByIds(VITAMIN_IDS, 'vitamin');
    if (vitaminRows.length) {
      sections.push({
        id: 'vitamins',
        title: 'Vitamins',
        accent: '#d97706',
        headerBg: 'rgba(217, 119, 6, 0.08)',
        columns: reportColumns,
        rows: vitaminRows,
      });
    }

    const mineralRows = buildRowsByIds(MINERALS_TRACE_IDS, 'mineral');
    if (mineralRows.length) {
      sections.push({
        id: 'minerals-trace-elements',
        title: 'Minerals and Trace Elements',
        accent: '#019e91',
        headerBg: 'rgba(5, 150, 105, 0.06)',
        columns: reportColumns,
        rows: mineralRows,
      });
    }

    const otherRows = buildRows(
      microsView.byStatus.all.filter((m) => {
        const id = m.nutrientId.trim().toLowerCase();
        return (
          !representedClinicalIds.has(id)
          && !INCLUDED_MICRO_IDS.has(id)
          && !EXCLUDED_MICRO_IDS.has(id)
          && !LIPID_COMPONENT_MICRO_IDS.has(id)
        );
      }),
      'other',
    );
    if (otherRows.length) {
      sections.push({
        id: 'other-micronutrients',
        title: 'Other Micronutrients',
        accent: '#475467',
        headerBg: 'rgba(71, 84, 103, 0.08)',
        columns: reportColumns,
        rows: otherRows,
      });
    }
  }

  return { sections, mealPlanReportMissingLabels };
}

function buildSuggestedRdaSections(analysisData: AnalysisData, primaryTypes?: DrvPrimaryType[], client?: Client, activeDrvRequirements?: unknown): NutritionSection[] {
  const saturatedFatTarget = getKpiByLabel(analysisData, 'Saturated Fat')?.target;

  const rawReqs = activeDrvRequirements || client?.drvRequirements;

  const getPrimaryTarget = (nutrientIdCandidates: string[], fallbackUnit: string) =>
    resolveDrvDisplayTarget(rawReqs, primaryTypes, nutrientIdCandidates, fallbackUnit);

  type SuggestedRdaRowConfig = Parameters<typeof buildSuggestedRdaRow>[0];
  type SuggestedRdaSectionConfig = {
    id: string;
    title: string;
    columns?: SuggestedRdaColumn[];
    rows: SuggestedRdaRowConfig[];
  };

  // Add new tables or extra row fields by extending this config array.
  const carbohydrateReq = findDrvRequirementByNutrientIds(rawReqs, ['carbohydrate', 'carbohydrates']);
  const totalFatReq = findDrvRequirementByNutrientIds(rawReqs, ['total_fat', 'fat', 'lipid']);
  const saturatedFatReq = findDrvRequirementByNutrientIds(rawReqs, ['saturated_fat']);
  const fibreReq = findDrvRequirementByNutrientIds(rawReqs, ['dietary_fibre', 'dietary_fiber', 'fiber']);

  const carbohydrateTarget = getPrimaryTarget(['carbohydrate', 'carbohydrates'], 'g');
  const proteinTarget = getPrimaryTarget(['protein'], 'g/day');
  const totalFatTarget = getPrimaryTarget(['total_fat', 'fat', 'lipid'], 'g');
  const saturatedFatRdaTarget = getPrimaryTarget(['saturated_fat'], 'g');
  const fibreTarget = getPrimaryTarget(['dietary_fibre', 'dietary_fiber', 'fiber'], 'g/day');
  const vitaminRows = buildSuggestedRdaMicronutrientRows(rawReqs, primaryTypes, [
    'vitaminsFatSoluble',
    'vitaminsWaterSoluble',
  ]);
  const mineralRows = buildSuggestedRdaMicronutrientRows(rawReqs, primaryTypes, [
    'mineralsMajor',
    'mineralsTrace',
  ]);

  const sectionConfigs: SuggestedRdaSectionConfig[] = [
    {
      id: 'suggested-rda-macros',
      title: 'Macronutrients',
      columns: suggestedRdaMacroColumns,
      rows: [
        {
          id: 'carbohydrates',
          label: 'Carbohydrates',
          current: analysisData.carbsTarget,
          ...resolveSuggestedRdaMacroRow(carbohydrateReq, carbohydrateTarget),
          targetCalculationExplanation: typeof carbohydrateReq?.targetCalculationExplanation === 'string'
            ? carbohydrateReq.targetCalculationExplanation
            : undefined,
        },
        {
          id: 'protein',
          label: 'Protein',
          current: analysisData.proteinTarget,
          ...resolveSuggestedRdaMacroRow(
            findDrvRequirementByNutrientIds(rawReqs, ['protein']),
            proteinTarget,
          ),
          targetCalculationExplanation: (() => {
            const requirement = findDrvRequirementByNutrientIds(rawReqs, ['protein']);
            return typeof requirement?.targetCalculationExplanation === 'string'
              ? requirement.targetCalculationExplanation
              : undefined;
          })(),
        },
        {
          id: 'total-fat',
          label: 'Total Fat',
          current: analysisData.fatTarget,
          ...resolveSuggestedRdaMacroRow(totalFatReq, totalFatTarget),
          targetCalculationExplanation: typeof totalFatReq?.targetCalculationExplanation === 'string'
            ? totalFatReq.targetCalculationExplanation
            : undefined,
        },
        {
          id: 'saturated-fat',
          label: 'Saturated Fat',
          current: saturatedFatTarget,
          ...resolveSuggestedRdaMacroRow(saturatedFatReq, saturatedFatRdaTarget, { ulFallback: true }),
          targetCalculationExplanation: typeof saturatedFatReq?.targetCalculationExplanation === 'string'
            ? saturatedFatReq.targetCalculationExplanation
            : undefined,
        },
        {
          id: 'dietary-fibre',
          label: 'Dietary Fibre',
          current: analysisData.fibreTarget,
          ...resolveSuggestedRdaMacroRow(fibreReq, fibreTarget, { aiFallback: true }),
          targetCalculationExplanation: typeof fibreReq?.targetCalculationExplanation === 'string'
            ? fibreReq.targetCalculationExplanation
            : undefined,
        },
      ],
    },
    {
      id: 'suggested-rda-vitamins',
      title: 'Micronutrients - Vitamins',
      columns: suggestedRdaMicronutrientColumns,
      rows: vitaminRows,
    },
    {
      id: 'suggested-rda-minerals',
      title: 'Micronutrients - Minerals',
      columns: suggestedRdaMicronutrientColumns,
      rows: mineralRows,
    },
  ];

  return sectionConfigs.map((sectionConfig) => ({
    id: sectionConfig.id,
    title: sectionConfig.title,
    columns: sectionConfig.columns ?? suggestedRdaColumns,
    rows: sectionConfig.rows.map((rowConfig) => buildSuggestedRdaRow(rowConfig)),
  }));
}

function formatSubtitlePart(part: string): string {
  const trimmed = part.trim();
  const cmMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*cm$/i);
  if (cmMatch) {
    const meters = Number(cmMatch[1]) / 100;
    return `${meters.toFixed(2)} m`;
  }

  const kgMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*kg$/i);
  if (kgMatch) {
    return `${kgMatch[1]} kg`;
  }

  if (/^(male|female)$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  return trimmed;
}

function formatContributionAmount(value: number, unit: string): string {
  const normalized = unit.trim().toLowerCase();
  if (normalized === 'kcal') return `${formatMetric(value, 0)} kcal`;
  if (normalized === 'l') return `${formatMetric(value, 1)} L`;
  if (normalized === 'mg') return `${formatMetric(value, 0)} mg`;
  if (normalized === 'μg' || normalized === 'ug') return `${formatMetric(value, 0)} μg`;
  return `${formatMetric(value, value < 10 ? 1 : 0)} ${unit}`.trim();
}

function splitContributionColumns(
  items: FoodContributionResult['items'],
): [FoodContributionResult['items'], FoodContributionResult['items'], FoodContributionResult['items']] {
  const c0: FoodContributionResult['items'] = [];
  const c1: FoodContributionResult['items'] = [];
  const c2: FoodContributionResult['items'] = [];
  items.forEach((item, i) => {
    if (i % 3 === 0) c0.push(item);
    else if (i % 3 === 1) c1.push(item);
    else c2.push(item);
  });
  return [c0, c1, c2];
}

export const NutritionReportTable: React.FC<NutritionReportTableProps> = ({
  analysisData,
  microsView,
  clientDisplayName,
  subtitleParts = [],
  variant = 'nutrition-report',
  primaryTypes,
  client,
  drvRequirements,
  nutritionalApiResponse,
  viewMode = 0,
  selectedDayIndex = 0,
  mealPlanId = '',
  onExportReport,
  exportReportLoading = false,
  isPdfExport = false,
  pdfMacroDistributionBlock,
  pdfExportVariant = 'full',
  hiddenNutrientKeys,
  onNutrientVisibilityChange,
  suggestedRdaMacrosAction,
}) => {
  const isSuggestedRda = variant === 'suggested-rda';
  const foodsRequestId = React.useRef(0);
  /** Tracks last view used for food-detail fetch so we only refetch when day/plan scope changes, not on row open. */
  const prevFoodDetailViewModeRef = React.useRef(viewMode);
  const prevFoodDetailDayRef = React.useRef(selectedDayIndex);
  const [contribution, setContribution] = React.useState<{
    activeRowKey: string | null;
    data: FoodContributionResult | null;
    loading: boolean;
    error: string | null;
  }>({ activeRowKey: null, data: null, loading: false, error: null });
  React.useEffect(() => {
    foodsRequestId.current += 1;
    setContribution({ activeRowKey: null, data: null, loading: false, error: null });
  }, [mealPlanId]);

  const handleReportRowClick = React.useCallback(
    (args: { section: NutritionSection; row: NutritionReportRow }) => {
      const rowKey = `${args.section.id}:${args.row.id}`;
      if (contribution.activeRowKey === rowKey) {
        setContribution({ activeRowKey: null, data: null, loading: false, error: null });
        return;
      }
      const planId = mealPlanId.trim();
      const nutrient = nutrientQueryParamFromRow(args.row);
      const base = buildFoodContributionsForReportRow(
        analysisData,
        { sectionId: args.section.id, sectionTitle: args.section.title, row: args.row },
        microsView ?? null,
        nutritionalApiResponse,
        viewMode,
        selectedDayIndex,
      );

      const rid = ++foodsRequestId.current;
      if (!planId || !nutrient) {
        setContribution({ activeRowKey: rowKey, data: base, loading: false, error: null });
        return;
      }

      setContribution({ activeRowKey: rowKey, data: base, loading: true, error: null });
      (async () => {
        try {
          const raw = await getMealPlanNutritionalAnalysisFoods(planId, {
            nutrient,
            limit: 18,
            day: viewMode === 0 ? selectedDayIndex + 1 : undefined,
          });
          if (foodsRequestId.current !== rid) return;
          const items = parseNutritionalAnalysisFoodsResponse(raw, { viewMode, selectedDayIndex });
          setContribution({
            activeRowKey: rowKey,
            data:
              items.length > 0
                ? { ...base, items }
                : base,
            loading: false,
            error: null,
          });
        } catch (err) {
          if (foodsRequestId.current !== rid) return;
          showErrorToast(err instanceof Error ? err : new Error('Food sources request failed'), 'Nutrient foods');
          setContribution({ activeRowKey: rowKey, data: base, loading: false, error: 'Could not load food sources from the server.' });
        }
      })();
    },
    [analysisData, contribution.activeRowKey, mealPlanId, microsView, nutritionalApiResponse, selectedDayIndex, viewMode],
  );
  const { sections: builtSections, mealPlanReportMissingLabels } = React.useMemo(() => {
    if (isSuggestedRda) {
      return {
        sections: buildSuggestedRdaSections(analysisData, primaryTypes, client, drvRequirements),
        mealPlanReportMissingLabels: [] as string[],
      };
    }
    return buildNutritionReportSections(analysisData, microsView, drvRequirements ?? client?.drvRequirements, primaryTypes);
  }, [analysisData, isSuggestedRda, primaryTypes, client, drvRequirements, microsView]);

  const sections = React.useMemo(() => {
    if (!isPdfExport || pdfExportVariant !== 'macros-only') return builtSections;
    return builtSections.filter((section) => PDF_MACRO_ONLY_SECTION_IDS.has(section.id));
  }, [builtSections, isPdfExport, pdfExportVariant]);

  const [nutrientVisibilityBusyKey, setNutrientVisibilityBusyKey] = React.useState<string | null>(null);

  const handleNutrientVisibilityToggle = React.useCallback(
    async (args: { section: NutritionSection; row: NutritionReportRow; hide: boolean }) => {
      if (!onNutrientVisibilityChange) return;
      const nutrientKey = nutrientQueryParamFromRow(args.row);
      if (!nutrientKey) return;
      const rowKey = `${args.section.id}:${args.row.id}`;
      setNutrientVisibilityBusyKey(nutrientKey);
      try {
        await onNutrientVisibilityChange({ nutrientKey, hide: args.hide });
        if (args.hide) {
          setContribution((c) =>
            c.activeRowKey === rowKey ? { activeRowKey: null, data: null, loading: false, error: null } : c,
          );
        }
      } finally {
        setNutrientVisibilityBusyKey(null);
      }
    },
    [onNutrientVisibilityChange],
  );

  React.useEffect(() => {
    if (isSuggestedRda || isPdfExport) return;

    const viewModeChanged = prevFoodDetailViewModeRef.current !== viewMode;
    const dayChanged = prevFoodDetailDayRef.current !== selectedDayIndex;
    prevFoodDetailViewModeRef.current = viewMode;
    prevFoodDetailDayRef.current = selectedDayIndex;
    if (!viewModeChanged && !dayChanged) return;

    const rowKey = contribution.activeRowKey;
    if (!rowKey) return;

    const colon = rowKey.indexOf(':');
    if (colon <= 0) return;
    const sectionId = rowKey.slice(0, colon);
    const rowId = rowKey.slice(colon + 1);
    const section = sections.find((s) => s.id === sectionId);
    const row = section?.rows.find((r) => r.id === rowId);
    if (!section || !row) return;

    const planId = mealPlanId.trim();
    const nutrient = nutrientQueryParamFromRow(row);
    const base = buildFoodContributionsForReportRow(
      analysisData,
      { sectionId: section.id, sectionTitle: section.title, row },
      microsView ?? null,
      nutritionalApiResponse,
      viewMode,
      selectedDayIndex,
    );

    const rid = ++foodsRequestId.current;
    if (!planId || !nutrient) {
      setContribution({ activeRowKey: rowKey, data: base, loading: false, error: null });
      return;
    }

    setContribution({ activeRowKey: rowKey, data: base, loading: true, error: null });
    void (async () => {
      try {
        const raw = await getMealPlanNutritionalAnalysisFoods(planId, {
          nutrient,
          limit: 18,
          day: viewMode === 0 ? selectedDayIndex + 1 : undefined,
        });
        if (foodsRequestId.current !== rid) return;
        const items = parseNutritionalAnalysisFoodsResponse(raw, { viewMode, selectedDayIndex });
        setContribution({
          activeRowKey: rowKey,
          data: items.length > 0 ? { ...base, items } : base,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (foodsRequestId.current !== rid) return;
        showErrorToast(err instanceof Error ? err : new Error('Food sources request failed'), 'Nutrient foods');
        setContribution({
          activeRowKey: rowKey,
          data: base,
          loading: false,
          error: 'Could not load food sources from the server.',
        });
      }
    })();
  }, [
    analysisData,
    contribution.activeRowKey,
    isPdfExport,
    isSuggestedRda,
    mealPlanId,
    microsView,
    nutritionalApiResponse,
    sections,
    selectedDayIndex,
    viewMode,
  ]);

  const toneStyles = isSuggestedRda ? suggestedRdaToneStyles : reportToneStyles;

  const sectionsForPdfExport = React.useMemo(() => {
    if (!isPdfExport) return sections;
    if (!hiddenNutrientKeys?.size) return sections;
    return omitHiddenNutrientsFromNutritionSectionsForPdf(sections, hiddenNutrientKeys);
  }, [sections, hiddenNutrientKeys, isPdfExport]);

  const summary = React.useMemo(() => {
    const sourceSections = isPdfExport ? sectionsForPdfExport : sections;
    const allRows = sourceSections.flatMap((section) => section.rows);
    return {
      met: allRows.filter((row) => row.tone === 'met').length,
      borderline: allRows.filter((row) => row.tone === 'borderline').length,
      deficient: allRows.filter((row) => row.tone === 'deficient').length,
      excess: allRows.filter((row) => row.tone === 'excess').length,
      toxic: allRows.filter((row) => row.tone === 'toxic').length,
    };
  }, [sections, sectionsForPdfExport, isPdfExport]);

  /** On screen, client + demographics already appear under "Detailed Analysis"; PDF keeps full identification. */
  const reportTitle = React.useMemo(() => {
    if (isPdfExport) {
      return `${(clientDisplayName || subtitleParts[0] || 'Client').trim()} — Nutrient Report`;
    }
    return 'Nutrient Report';
  }, [isPdfExport, clientDisplayName, subtitleParts]);

  const reportSubtitle = React.useMemo(() => {
    const parts = subtitleParts.slice(1).map(formatSubtitlePart).filter(Boolean);
    if (isPdfExport) {
      return parts.join(' - ');
    }
    return 'Compared to dietary reference values';
  }, [subtitleParts, isPdfExport]);
  const flagged = summary.deficient + summary.excess + summary.toxic;

  if (isSuggestedRda) {
    const micronutrientSectionIds = new Set(['suggested-rda-vitamins', 'suggested-rda-minerals']);
    const fullWidthSections = sections.filter((section) => !micronutrientSectionIds.has(section.id));
    const micronutrientSections = sections.filter((section) => micronutrientSectionIds.has(section.id));

    return (
      <Box sx={{ minWidth: 760, display: 'grid', gap: 1.35 }}>
        {fullWidthSections.map((section) => (
          <Box key={section.id} data-pdf-block="true" sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
            <NutritionReportSectionTable
              section={section}
              toneStyles={toneStyles}
              mode="suggested-rda"
              headerAction={section.id === 'suggested-rda-macros' ? suggestedRdaMacrosAction : undefined}
            />
          </Box>
        ))}
        {micronutrientSections.length ? (
          <Box
            data-pdf-block="true"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.35,
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            {micronutrientSections.map((section) => (
              <Box key={section.id} sx={{ minWidth: 0 }}>
                <NutritionReportSectionTable
                  section={section}
                  toneStyles={toneStyles}
                  mode="suggested-rda"
                />
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }

  if (isPdfExport) {
    return (
      <NutritionReportPdfView
        title={reportTitle}
        subtitle={reportSubtitle}
        sections={sectionsForPdfExport}
        toneStyles={toneStyles}
        macroDistributionBlock={pdfMacroDistributionBlock}
        flagged={flagged}
        borderline={summary.borderline}
        met={summary.met}
        mealPlanReportMissingLabels={mealPlanReportMissingLabels}
      />
    );
  }

  return (
    <Box sx={{ minWidth: 760, display: 'grid', gap: 1.75 }}>
      <Box
        data-pdf-block="true"
        sx={{
          border: '1px solid #D0D5DD',
          borderRadius: '20px',
          bgcolor: '#fff',
          px: { xs: 1.5, sm: 2.25 },
          py: { xs: 1.2, sm: 1.4 },
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 1fr) auto',
          },
          gap: 1.25,
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '1.2rem', sm: '1.05rem' },
              fontWeight: 700,
              color: '#1D2939',
              lineHeight: 1.2,
              overflowWrap: 'anywhere',
            }}
          >
            {reportTitle}
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              color: '#667085',
              lineHeight: 1.35,
              overflowWrap: 'anywhere',
            }}
          >
            {reportSubtitle}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.2,
            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
          }}
        >
          {[
            { label: 'Flagged', value: flagged, color: '#F04438' },
            { label: 'Borderline', value: summary.borderline, color: '#F79009' },
            { label: 'Met', value: summary.met, color: '#10b981' },
          ].map((item, index) => (
            <Box
              key={item.label}
              sx={{
                display: 'grid',
                justifyItems: 'center',
                pr: index < 2 ? 1.6 : 0,
                mr: index < 2 ? 0.1 : 0,
                borderRight: index < 2 ? '1px solid #E4E7EC' : 'none',
                minWidth: 84,
              }}
            >
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3', fontWeight: 600 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.65rem', lineHeight: 1.08, color: item.color, fontWeight: 700 }}>
                {item.value}
              </Typography>
            </Box>
          ))}

          {!isPdfExport ? (
            <Button
              variant="contained"
              size="small"
              onClick={onExportReport}
              disabled={exportReportLoading}
              startIcon={
                exportReportLoading ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
                ) : (
                  <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
                )
              }
              sx={{
                height: 40,
                minWidth: 126,
                borderRadius: '14px',
                bgcolor: '#20AEDC',
                color: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#199BC5', boxShadow: 'none' },
                '&.Mui-disabled': {
                  bgcolor: '#9fd7ea',
                  color: '#fff',
                },
              }}
            >
              {exportReportLoading ? 'Exporting...' : 'Export'}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box data-pdf-block="true" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, px: 0.5, py: 0.35 }}>
        {([
          ['met', `Met (${summary.met})`],
          ['borderline', `Borderline (${summary.borderline})`],
          ['deficient', `Deficient (${summary.deficient})`],
          ['excess', `Excess (${summary.excess})`],
          ['toxic', `Toxic (${summary.toxic})`],
        ] as Array<[RowTone, string]>).map(([tone, label]) => (
          <Box
            key={tone}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              py: 0.28,
              borderRadius: 999,
              bgcolor: toneStyles[tone].bg,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: toneStyles[tone].color,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {mealPlanReportMissingLabels.length > 0 ? (
        <Typography
          data-pdf-block="true"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.75rem',
            color: '#667085',
            lineHeight: 1.45,
            px: { xs: 0.5, sm: 0.75 },
            py: 0.5,
          }}
        >
          Not reported in this meal plan analysis: {mealPlanReportMissingLabels.join(', ')}.
        </Typography>
      ) : null}

      {sections.map((section) => (
        <Box key={section.id} data-pdf-block="true" sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          <NutritionReportSectionTable
            section={section}
            toneStyles={toneStyles}
            mode="nutrition-report"
            hideExpandColumn={isPdfExport}
            onReportRowClick={!isSuggestedRda && !isPdfExport ? handleReportRowClick : undefined}
            hiddenNutrientKeys={!isSuggestedRda && !isPdfExport ? hiddenNutrientKeys : undefined}
            nutrientVisibilityBusyKey={!isSuggestedRda && !isPdfExport ? nutrientVisibilityBusyKey : undefined}
            onNutrientVisibilityToggle={
              !isSuggestedRda && !isPdfExport && onNutrientVisibilityChange ? handleNutrientVisibilityToggle : undefined
            }
            expandedRowId={
              contribution.activeRowKey?.startsWith(`${section.id}:`)
                ? contribution.activeRowKey.slice(section.id.length + 1)
                : null
            }
            renderExpandedRow={({ section: expandedSection, row }) => {
              const rowKey = `${expandedSection.id}:${row.id}`;
              if (rowKey !== contribution.activeRowKey) return null;
              const data = contribution.data;
              const [c0, c1, c2] = splitContributionColumns(data?.items ?? []);
              const legendNoun = (
                data?.nutrientLabel?.match(/\(([^)]+)\)/)?.[1]
                ?? data?.nutrientLabel
                ?? 'nutrient'
              ).toLowerCase();

              return (
                <Box sx={{ px: '20px', pt: '8px', pb: '8px', bgcolor: '#fff' }}>
                  {contribution.error ? (
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#b42318', mb: 1.25 }}>
                      {contribution.error}
                    </Typography>
                  ) : null}
                  {contribution.loading ? (
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085', py: 1 }}>
                      Loading food sources...
                    </Typography>
                  ) : !data || data.items.length === 0 ? (
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085', py: 1 }}>
                      No per-food detail is available for this nutrient.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: { xs: 0, sm: '0 24px' },
                        rowGap: 0,
                      }}
                    >
                      {[c0, c1, c2].map((col, idx) => (
                        <Box key={`expanded-col-${idx}`} sx={{ minWidth: 0 }}>
                          {col.map((item, itemIndex) => (
                            <Box
                              key={item.name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                py: '6px',
                                minHeight: 29,
                                borderBottom: itemIndex < col.length - 1 ? '1px solid rgba(223, 242, 254, 0.5)' : 'none',
                              }}
                            >
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '12px', fontStyle: 'italic', lineHeight: '16px', color: '#0a0a0a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.name}
                              </Typography>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '12px', fontStyle: 'italic', lineHeight: '16px', color: item.hasContribution ? '#364153' : '#d1d5dc', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {formatContributionAmount(item.amount, data.unit)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  )}
                
                </Box>
              );
            }}
          />
        </Box>
      ))}
    </Box>
  );
};
